// components/views/CMRosterView.jsx
import { useState, useEffect, useRef } from 'react';
import { Field, Modal, Callout, EmptyState, Icons, SectionLabel, toast } from '../UI';
import { uid, today } from '../../utils/constants';
import { cmsDb } from '../../utils/store';

const EMPTY_ADDRESS = { label: '', street: '', city: '', state: 'CA', zip: '' };

const EMPTY_CM = {
  id: '', name: '', title: '', phone: '', email: '',
  agencyName: '', division: '',
  addresses: [{ ...EMPTY_ADDRESS, label: 'Main Office' }],
  active: true,
};

// ── Smart CM Form with duplicate detection + multi-address ──
function CMForm({ initial, onSave, onCancel, cms, apiKey, callAI, profile }) {
  const [f, setF] = useState({
    ...EMPTY_CM,
    agencyName: profile?.agencyName || '',
    ...initial,
    // Ensure addresses array exists — migrate legacy single address if needed
    addresses: initial.addresses?.length
      ? initial.addresses
      : initial.address
        ? [{ label: 'Main Office', street: initial.address, city: '', state: 'CA', zip: '' }]
        : [{ ...EMPTY_ADDRESS, label: 'Main Office' }],
  });
  const [nameMatches, setNameMatches] = useState([]);
  const [pennyMatch, setPennyMatch] = useState(null);
  const [checkingPenny, setCheckingPenny] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const nameTimer = useRef(null);
  const isNew = !initial.id;

  function upd(k, v) { setF(p => ({ ...p, [k]: v })); }

  function updAddr(idx, field, val) {
    setF(p => {
      const addrs = p.addresses.map((a, i) => i === idx ? { ...a, [field]: val } : a);
      return { ...p, addresses: addrs };
    });
  }

  function addAddress() {
    setF(p => ({ ...p, addresses: [...p.addresses, { ...EMPTY_ADDRESS }] }));
  }

  function removeAddress(idx) {
    setF(p => ({ ...p, addresses: p.addresses.filter((_, i) => i !== idx) }));
  }

  // ── Name change — duplicate check ──
  function handleNameChange(val) {
    upd('name', val);
    setDismissed(false);
    setPennyMatch(null);
    if (!val.trim() || !isNew) { setNameMatches([]); return; }
    const matches = cms.filter(cm =>
      cm.id !== initial.id &&
      cm.name.toLowerCase().includes(val.toLowerCase().trim())
    );
    setNameMatches(matches);
    clearTimeout(nameTimer.current);
    if (val.trim().length >= 4 && matches.length === 0 && apiKey) {
      nameTimer.current = setTimeout(() => checkPennyMatch(val), 800);
    }
  }

  async function checkPennyMatch(nameVal) {
    if (!apiKey || cms.length === 0) return;
    setCheckingPenny(true);
    try {
      const roster = cms.map(c => c.name).join('\n');
      const raw = await callAI(apiKey, {
        model: 'claude-haiku-4-5-20251001',
        max_tokens: 150,
        system: 'You are a data deduplication assistant. Given a new name and an existing roster, determine if the new name likely refers to the same person as any roster entry (accounting for abbreviations, nicknames, typos, name order differences). Respond ONLY with JSON: {"match": true/false, "rosterName": "exact name from roster if match", "reason": "brief reason"}',
        messages: [{ role: 'user', content: `New name: "${nameVal}"\n\nExisting roster:\n${roster}` }],
      });
      const cleaned = raw.replace(/```json|```/g, '').trim();
      const result = JSON.parse(cleaned);
      if (result.match && result.rosterName) {
        const matchedCm = cms.find(c => c.name === result.rosterName);
        if (matchedCm) setPennyMatch({ cm: matchedCm, reason: result.reason });
      }
    } catch (e) { /* silent fail */ }
    finally { setCheckingPenny(false); }
  }

  function applyExistingCM(cm) {
    setF({
      ...EMPTY_CM,
      agencyName: profile?.agencyName || '',
      ...cm,
      id: initial.id || '',
      name: f.name,
      addresses: cm.addresses?.length
        ? cm.addresses
        : cm.address
          ? [{ label: 'Main Office', street: cm.address, city: '', state: 'CA', zip: '' }]
          : [{ ...EMPTY_ADDRESS, label: 'Main Office' }],
    });
    setNameMatches([]);
    setPennyMatch(null);
    setDismissed(false);
    toast.info('Profile loaded from roster — review and save.');
  }

  const ok = f.name.trim() && f.email.trim();
  const showDupeWarning = isNew && !dismissed && (nameMatches.length > 0 || pennyMatch);

  return (
    <Modal
      title={initial.id ? 'Edit Contract Manager' : 'Add Contract Manager'}
      subtitle="This person will be selectable on any contract"
      onClose={onCancel}
      size="lg"
    >
      {/* ── Duplicate warning ── */}
      {showDupeWarning && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: 'var(--orange-50)', border: '1.5px solid var(--orange-200)', borderRadius: 'var(--r-md)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--orange-700)' }}>
              ⚠ {nameMatches.length > 0 ? 'Possible duplicate detected' : 'Penny found a possible match'}
            </div>
            <button onClick={() => setDismissed(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--orange-400)', fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
          {nameMatches.length > 0 && nameMatches.map(cm => (
            <div key={cm.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--white)', borderRadius: 'var(--r-sm)', marginBottom: 6, border: '1px solid var(--orange-200)' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-800)' }}>{cm.name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--slate-400)' }}>{cm.email}{cm.division ? ' · ' + cm.division : ''}</div>
              </div>
              <button className="btn btn-gold btn-sm" onClick={() => applyExistingCM(cm)}>Load Profile</button>
            </div>
          ))}
          {pennyMatch && nameMatches.length === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--white)', borderRadius: 'var(--r-sm)', border: '1px solid var(--orange-200)' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-800)' }}>{pennyMatch.cm.name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--slate-400)' }}>{pennyMatch.reason}</div>
              </div>
              <button className="btn btn-gold btn-sm" onClick={() => applyExistingCM(pennyMatch.cm)}>Load Profile</button>
            </div>
          )}
          {checkingPenny && (
            <div style={{ fontSize: 12, color: 'var(--orange-500)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <div className="spin spin-sm" style={{ borderTopColor: 'var(--orange-500)' }} />
              Penny is checking the roster…
            </div>
          )}
        </div>
      )}

      <div className="form-grid">
        {/* Name */}
        <div style={{ gridColumn: 'span 2' }}>
          <Field label="Full Name" value={f.name}
            onChange={handleNameChange} required
            hint={isNew && nameMatches.length === 0 && !pennyMatch && f.name.trim().length > 2 ? '✓ No duplicates found in roster' : undefined} />
        </div>

        <Field label="Title / Classification" value={f.title} onChange={v => upd('title', v)}
          placeholder="e.g., Associate Governmental Program Analyst" />
        <Field label="Phone" value={f.phone} onChange={v => upd('phone', v)} />
        <Field label="Email" value={f.email} onChange={v => upd('email', v)} required />
        <Field label="Division / Unit" value={f.division} onChange={v => upd('division', v)}
          placeholder="e.g., Admin Services Division" />
        <div style={{ gridColumn: 'span 2' }}>
          <Field label="Agency Name" value={f.agencyName} onChange={v => upd('agencyName', v)}
            placeholder="e.g., Office of Energy Infrastructure Safety" />
        </div>
      </div>

      {/* ── Multi-address section ── */}
      <div style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate-600)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Mailing Addresses
          </div>
          <button className="btn btn-xs btn-ghost" onClick={addAddress}
            style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icons.plus /> Add Address
          </button>
        </div>

        {f.addresses.map((addr, idx) => (
          <div key={idx} style={{
            marginBottom: 12, padding: '14px 16px',
            background: 'var(--slate-50)', border: '1px solid var(--slate-200)',
            borderRadius: 'var(--r-md)', position: 'relative',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <input
                value={addr.label}
                onChange={e => updAddr(idx, 'label', e.target.value)}
                placeholder="Label (e.g. Main Office, Field Office)"
                style={{
                  fontSize: 12, fontWeight: 700, color: 'var(--navy-700)',
                  background: 'none', border: 'none', outline: 'none',
                  borderBottom: '1px dashed var(--slate-300)', width: '60%',
                  paddingBottom: 2,
                }}
              />
              {f.addresses.length > 1 && (
                <button onClick={() => removeAddress(idx)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red-400)', fontSize: 16, lineHeight: 1 }}>×</button>
              )}
            </div>
            <div className="form-grid" style={{ gap: 8 }}>
              <div style={{ gridColumn: 'span 2' }}>
                <Field label="Street Address" value={addr.street}
                  onChange={v => updAddr(idx, 'street', v)}
                  placeholder="e.g., 2151 Berkeley Way" />
              </div>
              <Field label="City" value={addr.city}
                onChange={v => updAddr(idx, 'city', v)}
                placeholder="Sacramento" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Field label="State" value={addr.state}
                  onChange={v => updAddr(idx, 'state', v)}
                  placeholder="CA" />
                <Field label="ZIP" value={addr.zip}
                  onChange={v => updAddr(idx, 'zip', v)}
                  placeholder="95814" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="btn-row" style={{ marginTop: 8 }}>
        <button className="btn btn-gold" disabled={!ok} onClick={() => onSave(f)}>
          <Icons.check /> {initial.id ? 'Save Changes' : 'Add to Roster'}
        </button>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </Modal>
  );
}

export default function CMRosterView({ cms, onUpdate, apiKey, callAI, profile }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = cms.filter(cm =>
    !search || [cm.name, cm.email, cm.division, cm.agencyName].some(f => f?.toLowerCase().includes(search.toLowerCase()))
  );

  async function saveCM(f) {
    // Build a clean object — keep addresses array, drop legacy address string
    const { address: _drop, ...rest } = f;
    const payload = {
      ...rest,
      id: f.id || uid(),
      addresses: f.addresses || [],
    };
    const ok = await cmsDb.upsertOne(payload);
    if (ok) {
      onUpdate(f.id
        ? cms.map(c => c.id === f.id ? payload : c)
        : [...cms, payload]
      );
      setShowForm(false);
      setEditing(null);
      toast.success(f.id ? 'Contract manager updated' : 'Contract manager added');
    } else {
      toast.error('Save failed — please try again');
    }
  }

  async function removeCM(id) {
    if (!window.confirm('Remove this contract manager from the roster?')) return;
    const ok = await cmsDb.remove(id);
    if (ok) {
      onUpdate(cms.filter(c => c.id !== id));
      const cm = cms.find(c => c.id === id);
      toast.info((cm?.name || 'Contract manager') + ' removed from roster');
    } else {
      toast.error('Failed to remove — please try again');
    }
  }

  async function toggleActive(id) {
    const cm = cms.find(c => c.id === id);
    const updated = { ...cm, active: !cm.active };
    const ok = await cmsDb.upsertOne(updated);
    if (ok) onUpdate(cms.map(c => c.id === id ? updated : c));
  }

  // Helper: get first address as display string
  function primaryAddress(cm) {
    if (cm.addresses?.length) {
      const a = cm.addresses[0];
      const parts = [a.street, a.city, a.state, a.zip].filter(Boolean);
      return parts.join(', ') || null;
    }
    return cm.address || null;
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">State CM Roster</h1>
          <p className="page-subtitle">
            {cms.length} contract manager{cms.length !== 1 ? 's' : ''} on file —
            select one when creating or editing a contract
          </p>
        </div>
        <button className="btn btn-gold" onClick={() => { setEditing(null); setShowForm(true); }}>
          <Icons.plus /> Add Contract Manager
        </button>
      </div>

      <Callout type="blue" style={{ marginBottom: 20 }}>
        Add all State Contract Managers who may be assigned to contracts. When you create or edit a contract,
        you can pick from this list — their contact info will auto-fill the STD 215 and Exhibit A CM table.
        Penny will detect duplicates and suggest existing profiles automatically.
      </Callout>

      {cms.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="👤"
            title="No contract managers yet"
            subtitle="Add the State CMs who will be assigned to contracts."
            action={
              <button className="btn btn-gold" onClick={() => setShowForm(true)}>
                <Icons.plus /> Add First Contract Manager
              </button>
            }
          />
        </div>
      ) : (
        <div className="card">
          {cms.length > 3 && (
            <div className="search-bar" style={{ marginBottom: 16, maxWidth: '100%' }}>
              <span className="search-bar-icon"><Icons.search /></span>
              <input
                placeholder="Search by name, email, division, or agency…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          )}

          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Title</th>
                <th>Agency / Division</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Addresses</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(cm => (
                <tr key={cm.id} style={{ opacity: cm.active === false ? .5 : 1 }}>
                  <td style={{ fontWeight: 600 }}>{cm.name}</td>
                  <td style={{ fontSize: 12, color: 'var(--slate-500)' }}>{cm.title || '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--slate-500)' }}>
                    {cm.agencyName && <div style={{ fontWeight: 600, color: 'var(--navy-700)' }}>{cm.agencyName}</div>}
                    {cm.division && <div>{cm.division}</div>}
                    {!cm.agencyName && !cm.division && '—'}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{cm.phone || '—'}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{cm.email}</td>
                  <td style={{ fontSize: 11.5, color: 'var(--slate-500)' }}>
                    {cm.addresses?.length
                      ? cm.addresses.map((a, i) => (
                          <div key={i}>{a.label ? <strong>{a.label}: </strong> : null}{[a.street, a.city, a.state, a.zip].filter(Boolean).join(', ') || '—'}</div>
                        ))
                      : primaryAddress(cm) || '—'
                    }
                  </td>
                  <td>
                    <span className={`badge ${cm.active !== false ? 'badge-green' : 'badge-gray'}`} style={{ fontSize: 9.5 }}>
                      {cm.active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button className="btn btn-xs btn-ghost" onClick={() => { setEditing(cm); setShowForm(true); }}><Icons.edit /></button>
                      <button className="btn btn-xs btn-ghost"
                        title={cm.active !== false ? 'Mark inactive' : 'Mark active'}
                        onClick={() => toggleActive(cm.id)}
                        style={{ color: cm.active !== false ? 'var(--orange-600)' : 'var(--green-600)' }}>
                        {cm.active !== false ? 'Deactivate' : 'Activate'}
                      </button>
                      <button className="btn btn-xs btn-danger" onClick={() => removeCM(cm.id)}><Icons.trash /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filtered.length === 0 && search && (
            <p style={{ textAlign: 'center', color: 'var(--slate-400)', padding: 16, fontSize: 13 }}>
              No contract managers match "{search}"
            </p>
          )}
        </div>
      )}

      {/* CM Card view */}
      {cms.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginTop: 16 }}>
          {filtered.map(cm => (
            <div key={cm.id} className="card" style={{
              marginBottom: 0,
              opacity: cm.active === false ? .6 : 1,
              borderLeft: '3px solid ' + (cm.active !== false ? 'var(--navy-500)' : 'var(--slate-300)'),
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{cm.name}</div>
                  {cm.title && <div style={{ fontSize: 11.5, color: 'var(--slate-500)', marginTop: 2 }}>{cm.title}</div>}
                  {cm.agencyName && <div style={{ fontSize: 11, color: 'var(--navy-600)', fontWeight: 600, marginTop: 1 }}>{cm.agencyName}</div>}
                  {cm.division && <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 1 }}>{cm.division}</div>}
                </div>
                <span className={`badge ${cm.active !== false ? 'badge-green' : 'badge-gray'}`} style={{ fontSize: 9, flexShrink: 0 }}>
                  {cm.active !== false ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--slate-600)', display: 'flex', flexDirection: 'column', gap: 3 }}>
                {cm.phone && <span>📞 {cm.phone}</span>}
                {cm.email && <span>✉ {cm.email}</span>}
                {cm.addresses?.length > 0 && cm.addresses.map((a, i) => {
                  const line = [a.street, a.city, a.state, a.zip].filter(Boolean).join(', ');
                  return line ? (
                    <span key={i} style={{ fontSize: 11, color: 'var(--slate-400)' }}>
                      📍 {a.label ? <strong>{a.label}:</strong> : null} {line}
                    </span>
                  ) : null;
                })}
                {(!cm.addresses?.length) && primaryAddress(cm) && (
                  <span style={{ fontSize: 11, color: 'var(--slate-400)' }}>📍 {primaryAddress(cm)}</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                <button className="btn btn-xs btn-ghost" style={{ flex: 1 }} onClick={() => { setEditing(cm); setShowForm(true); }}>
                  <Icons.edit /> Edit
                </button>
                <button className="btn btn-xs btn-danger" onClick={() => removeCM(cm.id)}><Icons.trash /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <CMForm
          initial={editing || {}}
          onSave={saveCM}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          cms={cms}
          apiKey={apiKey}
          callAI={callAI}
          profile={profile}
        />
      )}
    </div>
  );
}
